import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { calendarDate } from "@/shared/utils/calendarDate";
import { useAvailabilities, useAvailabilityOverrides } from "../hooks/useAvailabilities";
import { overrideForDay, scheduleWeekdays, scopedSchedules } from "../utils/businessHours";
const noTimesMessage = "No available times for this day. Choose another date.";

export function DateAvailabilityEmptyState({ dateKey, locationId }: { dateKey: string; locationId: string }) {
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();
  const scope = { userId: user?.id ?? "", businessId: businessId ?? "", locationId };
  const availability = useAvailabilities(scope);
  const overrides = useAvailabilityOverrides({ ...scope, startDate: dateKey, endDate: dateKey });

  if (availability.isFetching || overrides.isFetching) return <RowSkeleton />;
  const error = availability.error ?? overrides.error;
  if (error) {
    return (
      <ErrorState
        title="Couldn't check working hours"
        message={error.message}
        onRetry={() => { void availability.refetch(); void overrides.refetch(); }}
      />
    );
  }

  const { schedules, active: schedule } = scopedSchedules(availability.data ?? [], scope);
  let message = noTimesMessage;

  if (!schedule) {
    message = schedules.length
      ? "Your working hours are inactive for this location."
      : "You haven't set up working hours for this location.";
  } else {
    const override = overrideForDay(overrides.data ?? [], scope, dateKey);
    if (override?.overrideType === "unavailable") {
      message = override.reason?.trim()
        ? `You've marked this date as unavailable. Reason: ${override.reason.trim()}`
        : "You've marked this date as unavailable. Choose another date.";
    } else if (!override) {
      const day = scheduleWeekdays[calendarDate(dateKey).getDay()];
      if (schedule.schedule[day].length === 0) {
        const label = day.charAt(0).toUpperCase() + day.slice(1);
        message = `You don't work on ${label}s at this location. Choose another date or update your working hours.`;
      }
    }
  }

  return <EmptyState message={message} />;
}
